'use client';

import classnames from 'classnames';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React, { forwardRef, useMemo, useState } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import { GenreDto } from '@cineamo/api/models/movie/GenreDto.types';

import { getLanguageNameFromLocale } from '@cineamo/ui/src/helpers/locale-helper';
import { getAgeRatingIcon } from '@cineamo/ui/src/helpers/movie-helper';
import { formatDuration } from '@cineamo/ui/src/helpers/showing-helper';
import { notEmptyValue } from '@cineamo/ui/src/helpers/type-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconInfo24 } from '@cineamo/ui/icons/ic-info';
import ImageGallery from '@cineamo/ui/src/image/ImageGallery';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import MobileOrDesktopRenderer from '@cineamo/composables/MobileOrDesktopRenderer';
import CenterLayout from '@cineamo/composables/src/CenterLayout';
import MovieBackdropImage from '@cineamo/composables/src/movie/MovieBackdropImage';
import MovieInfoSection from '@cineamo/composables/src/movie/MovieInfoSection';
import MovieOverview from '@cineamo/composables/src/movie/MovieOverview';
import MoviePosterImage from '@cineamo/composables/src/movie/MoviePosterImage';

export type MovieHeaderProps = {
    className?: string;
};

/**
 * Movie header component for movie-landingpage app.
 * Follows the same structure as web app's MovieHeader but without authentication features.
 * Uses shared components: MovieBackdropImage, MoviePosterImage, MovieInfoSection.
 */
const MovieHeader: React.ForwardRefRenderFunction<HTMLDivElement, MovieHeaderProps> = ({ className }, ref) => {
    const t = useTranslations();
    const format = useFormat();
    const locale = useLocale();
    const { movie, movieDto, domainConfiguration } = useDomainConfiguration();
    const { originalTitle } = movieDto;
    const {
        title: movieTitle,
        originalLanguage,
        releaseYear,
        releaseDate,
        runtime,
        ageRating,
        posterImageURL_W780: moviePosterImage,
        posterBlurDataImage: moviePosterBlurDataImage,
        backdropImageURL_ORIGINAL: movieBackdropImage,
        backdropBlurDataImage: movieBackdropBlurDataImage,
        genres: movieGenres,
        productionCountries: movieProductionCountries
    } = movie;

    const overview = domainConfiguration.movieOverview?.[locale] || movie.overview;

    const ageRatingElement = ageRating
        ? getAgeRatingIcon(ageRating, 'DE', {
              isThemeLight: false
          })
        : null;
    const [posterGalleryOpen, setPosterGalleryOpen] = useState(false);

    const backdropImageUrl = domainConfiguration.movieBackdropUrl || movieBackdropImage;
    const distributor = domainConfiguration.distributor;
    const moviePosterAndTitleSection = useMemo(
        () => (
            <div className={classnames('absolute top-164 left-9 sm:left-38 lg:left-16 right-0 z-10')}>
                <div className={'flex space-x-24'}>
                    <div
                        className="cursor-pointer"
                        onClick={() => setPosterGalleryOpen(true)}>
                        <MoviePosterImage
                            className="w-110 md:w-150 h-160 md:h-225 flex-none rounded-8 overflow-clip"
                            movieTitle={movieTitle}
                            moviePosterImage={moviePosterImage}
                            moviePosterBlurDataImage={moviePosterBlurDataImage}
                        />
                    </div>
                    <div className="flex flex-col justify-end h-full space-y-0 lg:space-y-6 self-end">
                        <h1 className="text-headline-20 md:text-headline-32">{movieTitle}</h1>
                        <div className="flex flex-row items-center space-x-10 text-body-bold-12 md:text-headline-18">
                            {ageRatingElement}
                            <DotSeparatedLabel
                                className="opacity-50"
                                labels={[
                                    releaseYear,
                                    runtime && runtime > 0 ? formatDuration(runtime, t) : undefined
                                ].filter(notEmptyValue)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        ),
        [movieTitle, moviePosterImage, moviePosterBlurDataImage, ageRatingElement, releaseYear, runtime, t]
    );

    const movieDetailsContent = useMemo(() => {
        const movieOriginalLanguage = originalLanguage
            ? getLanguageNameFromLocale(originalLanguage, locale)
            : undefined;
        const director = movieDto.credits?.crew?.find((crewMember) => crewMember.job === 'Director')?.name;

        return [
            originalTitle && { label: t('movie.original-title'), value: originalTitle },
            movieOriginalLanguage && { label: t('movie.original-language'), value: movieOriginalLanguage },
            director && { label: t('movie.director'), value: director },
            movieProductionCountries && {
                label: t('movie.production-country'),
                value: movieProductionCountries
            },
            releaseDate && {
                label: t('movie.release-date'),
                value: format.dateTime.formatDateNumbersOnly(releaseDate)
            },
            distributor?.logoUrl && {
                value: '',
                label: (
                    <Image
                        src={distributor?.logoUrl}
                        width={200}
                        height={72}
                        className="max-h-[72px] object-left object-contain"
                        alt={distributor?.name}
                    />
                )
            }
        ].filter(notEmptyValue);
    }, [
        format.dateTime,
        movieProductionCountries,
        releaseDate,
        originalLanguage,
        originalTitle,
        t,
        locale,
        movieDto.credits?.crew,
        distributor?.logoUrl,
        distributor?.name
    ]);

    const overviewSlot = useMemo(
        () => (
            <MovieOverview
                className="whitespace-pre-wrap"
                overview={overview}
            />
        ),
        [overview]
    );

    const customGenres = (domainConfiguration.genres ?? []).map((genre) => ({
        id: genre,
        name: t(`genres.custom-genre-${genre}`)
    }));
    const genres: GenreDto[] = [...customGenres, ...movieGenres];
    const hint = domainConfiguration.movieHint?.[locale];

    const hintSlot = useMemo(() => {
        return (
            hint && (
                <div className="p-12 rounded-8 bg-[rgba(var(--surface-color-rgb),0.1)] text-[var(--primary-color)] italic text-body-bold-14 flex flex-row items-center gap-12 mt-24">
                    <span>
                        <IconInfo24 fill={domainConfiguration.style.primaryColor} />
                    </span>
                    {hint}
                </div>
            )
        );
    }, [hint, domainConfiguration.style.primaryColor]);

    return (
        <header ref={ref}>
            {posterGalleryOpen && moviePosterImage && (
                <ImageGallery
                    images={[moviePosterImage]}
                    open={true}
                    onClose={() => setPosterGalleryOpen(false)}
                    currentIndex={0}
                />
            )}
            <div className="flex flex-col overflow-hidden lg:pt-24">
                <MobileOrDesktopRenderer
                    mobileContent={
                        <div>
                            <div className="relative w-full h-full">
                                <div className={classnames(className, 'relative w-full h-300')}>
                                    <MovieBackdropImage
                                        movieTitle={movieTitle}
                                        movieBackdropImage={backdropImageUrl}
                                        movieBackdropBlurDataImage={movieBackdropBlurDataImage}
                                    />
                                </div>
                                {moviePosterAndTitleSection}
                            </div>
                            <CenterLayout>
                                <MovieInfoSection
                                    cast={movieDto.credits?.cast}
                                    overviewSlot={overviewSlot}
                                    genres={genres}
                                    details={movieDetailsContent}
                                    hintSlot={hintSlot}
                                />
                            </CenterLayout>
                        </div>
                    }
                    desktopContent={
                        <div className="relative w-full">
                            <div className={classnames(className, 'relative w-full h-300')}>
                                <MovieBackdropImage
                                    className={'rounded-t-16'}
                                    movieTitle={movieTitle}
                                    movieBackdropImage={backdropImageUrl}
                                    movieBackdropBlurDataImage={movieBackdropBlurDataImage}
                                />
                            </div>
                            {moviePosterAndTitleSection}
                            <MovieInfoSection
                                cast={movieDto.credits?.cast}
                                overviewSlot={overviewSlot}
                                genres={genres}
                                details={movieDetailsContent}
                                hintSlot={hintSlot}
                            />
                        </div>
                    }
                />
            </div>
        </header>
    );
};

export default forwardRef(MovieHeader);
